import { buildCriterionExpression } from '@coveo/headless';
import { useGeolocationState } from 'contexts/GeolocationContext';
import React from 'react';

import { useBreakpoint } from '@/hooks/useBreakpoint';
import { useSearchParams } from '@/hooks/useSearchParams';

import { useLocationSearchContext } from '../../contexts/LocationSearchContext';
import { useLocationUIContext } from '../../contexts/LocationUIContext';
import { LocationFacet } from './LocationFacet';
import { LocationFilterDesktop } from './LocationFilterDesktop';
import { LocationFilterMobile } from './LocationFilterMobile';

const LocationFilter = ({ ...props }) => {
    const breakpoint = useBreakpoint();
    const isMobile = !breakpoint?.large;

    const {
        coveo: {
            controller: { sort, facet },
        },
        handleSingleFilter,
        handleFilterResetAll,
        sortCriteria,
    } = useLocationSearchContext();

    const { isLocationCategory } = useLocationUIContext();
    const geolocationState = useGeolocationState();

    const [sortState, setSortState] = React.useState(sort.state);
    const [specialtyState, setSpecialtyState] = React.useState();
    const [conditionState, setConditionState] = React.useState();
    const [treatmentState, setTreatmentState] = React.useState();

    const getCriterionFromName = React.useCallback(
        (name) => sortCriteria.find(([criterionName]) => criterionName === name),
        [sortCriteria],
    );

    const getCurrentCriterion = React.useCallback(() => {
        return sortCriteria.find(
            ([, criterion]) => sortState.sortCriteria === buildCriterionExpression(criterion),
        );
    }, [sortCriteria, sortState.sortCriteria]);

    React.useEffect(() => {
        const unsubscribe = sort.subscribe(() => setSortState(sort.state));
        return () => {
            unsubscribe();
        };
    }, [sort]);

    React.useEffect(() => {
        const unsubscribe = facet.specialty.subscribe(() => {
            setSpecialtyState(facet.specialty.state);
        });
        return () => {
            unsubscribe();
        };
    }, [facet.specialty]);

    React.useEffect(() => {
        const unsubscribe = facet.condition.subscribe(() => {
            setConditionState(facet.condition.state);
        });
        return () => {
            unsubscribe();
        };
    }, [facet.condition]);

    React.useEffect(() => {
        const unsubscribe = facet.treatment.subscribe(() => {
            setTreatmentState(facet.treatment.state);
        });
        return () => {
            unsubscribe();
        };
    }, [facet.treatment]);

    const handleSort = React.useCallback(
        (value) => {
            handleSingleFilter(
                { sort: getCriterionFromName(value)[0] },
                { componentTitle: 'SORT BY', actionLabel: value },
            );
        },
        [getCriterionFromName, handleSingleFilter],
    );

    const searchParams = useSearchParams();
    const disabledDistanceOptions =
        !searchParams.has('zip-code') &&
        !searchParams.has('city') &&
        !geolocationState?.recentSearch;
    const urlDistance = searchParams.get('distance') ?? '';

    const [distance, setDistance] = React.useState(urlDistance);
    React.useEffect(() => {
        setDistance(urlDistance);
    }, [urlDistance]);

    const accordionItems = React.useMemo(
        () => [
            ...(!isLocationCategory
                ? [
                      {
                          id: 'type',
                          title: 'Location Type',
                          titleIndicator: null,
                          children: (
                              <LocationFacet.Root
                                  urlParamName={'type'}
                                  variant={'radio'}
                                  controller={facet.type}
                              />
                          ),
                      },
                  ]
                : []),
            ...(specialtyState?.values?.length > 0
                ? [
                      {
                          id: 'specialty',
                          title: 'Specialty',
                          titleIndicator: <LocationFacet.Badge fieldName={'locspecialties'} />,
                          children: (
                              <LocationFacet.Root
                                  urlParamName={'specialty'}
                                  variant={'checkbox'}
                                  controller={facet.specialty}
                                  analytics={{
                                      componentTitle: 'Specialty',
                                  }}
                              />
                          ),
                      },
                  ]
                : []),
            ...(conditionState?.values?.length > 0
                ? [
                      {
                          id: 'condition',
                          title: 'Condition',
                          titleIndicator: <LocationFacet.Badge fieldName={'loccondition'} />,
                          children: (
                              <LocationFacet.Root
                                  urlParamName={'condition'}
                                  variant={'checkbox'}
                                  controller={facet.condition}
                                  analytics={{
                                      componentTitle: 'Condition',
                                  }}
                              />
                          ),
                      },
                  ]
                : []),
            ...(treatmentState?.values?.length > 0
                ? [
                      {
                          id: 'treatment',
                          title: 'Treatment',
                          titleIndicator: <LocationFacet.Badge fieldName={'locservices'} />,
                          children: (
                              <LocationFacet.Root
                                  urlParamName={'treatment'}
                                  variant={'checkbox'}
                                  controller={facet.treatment}
                                  analytics={{
                                      componentTitle: 'Treatment',
                                  }}
                              />
                          ),
                      },
                  ]
                : []),
            // {
            //     id: 'accepted-insurance',
            //     title: 'Accepted Insurance',
            //     titleIndicator: <LocationFacet.Badge fieldName={'locacceptedinsurance'} />,
            //     children: (
            //         <LocationFacet.Root
            //             urlParamName={'acc-ins'}
            //             variant={'checkbox'}
            //             controller={facet.acceptedInsurance}
            //         />
            //     ),
            // },
        ],
        [isLocationCategory, facet, specialtyState, conditionState, treatmentState],
    );

    return (
        <>
            {isMobile && (
                <LocationFilterMobile
                    {...props}
                    accordionItems={accordionItems}
                    sortValue={getCurrentCriterion()[0]}
                    sortCriteria={sortCriteria}
                    handleSort={handleSort}
                    distance={distance}
                    setDistance={setDistance}
                    disabledDistanceOptions={disabledDistanceOptions}
                    handleSingleFilter={handleSingleFilter}
                    handleResetAll={handleFilterResetAll}
                />
            )}
            {!isMobile && (
                <LocationFilterDesktop
                    {...props}
                    accordionItems={accordionItems}
                    sortValue={getCurrentCriterion()[0]}
                    sortCriteria={sortCriteria}
                    distance={distance}
                    setDistance={setDistance}
                    disabledDistanceOptions={disabledDistanceOptions}
                    handleSort={handleSort}
                    handleSingleFilter={handleSingleFilter}
                    handleResetAll={handleFilterResetAll}
                />
            )}
        </>
    );
};

LocationFilter.propTypes = {};

export default LocationFilter;
